// src/firebase/questionPapers.js
// Head-marker-authored question paper per homework type, shown to a student
// who chooses to upload a completed PDF instead of using the exam simulator
// (they'd otherwise have no way to see what the task is asking). Same
// homework type/question paper is shared by every cohort. Mirrors
// solutions.js: the Storage path (not a tokenized URL) is stored in
// Firestore, and an authenticated URL is generated on demand via
// submissions.js's getAuthenticatedFileUrl.

import { doc, getDoc, setDoc, deleteDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytesResumable, deleteObject } from 'firebase/storage';
import { db, storage } from './config';
import { getCurrentUserOrgId } from './orgs';
import { getHomeworkNames, sortHomeworkTypes } from './settings';

// Each homework type's question paper lives at
// orgs/{orgId}/questionPapers/{homeworkType}. Every function below accepts
// an optional trailing orgId that overrides the auto-resolved "current
// signed-in user's org" - see settings.js for the same pattern and when to
// pass it explicitly.
const getQuestionPaperRef = async (homeworkType, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  return doc(db, 'orgs', resolvedOrgId, 'questionPapers', homeworkType);
};

/**
 * Get the question paper for a single homework type, or null if none has
 * been uploaded yet.
 */
export const getQuestionPaper = async (homeworkType, orgId) => {
  try {
    const docRef = await getQuestionPaperRef(homeworkType, orgId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? docSnap.data() : null;
  } catch (error) {
    console.error('Get question paper error:', error);
    return null;
  }
};

/**
 * Get question papers for every homework type in the org, as
 * { [homeworkType]: { filePath, fileName, updatedAt } | null }.
 */
export const getAllQuestionPapers = async (orgId) => {
  try {
    const resolvedOrgId = orgId || await getCurrentUserOrgId();
    const names = await getHomeworkNames(resolvedOrgId);
    const homeworkTypes = sortHomeworkTypes(Object.keys(names));
    const results = await Promise.all(homeworkTypes.map(type => getQuestionPaper(type, resolvedOrgId)));
    return Object.fromEntries(homeworkTypes.map((type, i) => [type, results[i]]));
  } catch (error) {
    console.error('Get all question papers error:', error);
    return {};
  }
};

/**
 * Upload (or replace) the question paper file for a homework type. Any
 * previous file for the same type is deleted from Storage first so a
 * replace doesn't leak the old file.
 */
export const uploadQuestionPaper = async (homeworkType, file, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  const existing = await getQuestionPaper(homeworkType, resolvedOrgId);
  if (existing?.filePath) {
    await deleteObject(ref(storage, existing.filePath)).catch(() => {});
  }

  const filePath = `questionPapers/${resolvedOrgId}/${homeworkType}/${Date.now()}_${file.name}`;
  await uploadBytesResumable(ref(storage, filePath), file);

  const docRef = await getQuestionPaperRef(homeworkType, resolvedOrgId);
  const data = { filePath, fileName: file.name, updatedAt: serverTimestamp() };
  await setDoc(docRef, data);
  return data;
};

/**
 * Remove the question paper for a homework type entirely.
 */
export const deleteQuestionPaper = async (homeworkType, orgId) => {
  const resolvedOrgId = orgId || await getCurrentUserOrgId();
  const existing = await getQuestionPaper(homeworkType, resolvedOrgId);
  if (existing?.filePath) {
    await deleteObject(ref(storage, existing.filePath)).catch(() => {});
  }
  const docRef = await getQuestionPaperRef(homeworkType, resolvedOrgId);
  await deleteDoc(docRef);
};
